import { apiRequest } from './http-client';
import type { VobWebformSubmitPayload } from './types';

/** Default SendGrid template id for VOB webform facility notifications. */
export const VOB_WEBFORM_EMAIL_TEMPLATE = 'd-d3e7f2daf8874f59bebd2a0f28afff34';

const DEFAULT_SENDER = 'no-reply@finpay.net';
const EMAIL_SUBJECT = 'Insurance verification webform submission';
const EMAIL_PREHEADER = 'New VOB webform submission';

export function parseSendToAddresses(sendTo: string): string[] {
  return sendTo
    .split(/[,;]/)
    .map((address) => address.trim())
    .filter(Boolean);
}

function lastNameInitial(lastName?: string): string {
  const trimmed = lastName?.trim();
  if (!trimmed) {
    return '';
  }
  return trimmed.charAt(0).toUpperCase();
}

/** Maps API gateway base URL to FinPass admin portal base URL. */
export function resolveAdminPortalBase(apiBase: string): string {
  const normalized = apiBase.replace(/\/$/, '');
  if (normalized === 'https://api.finpay.net') {
    return 'https://admin.finpay.net';
  }

  const envMatch = normalized.match(/^https?:\/\/api\.([^.]+)\.finpay\.net$/);
  if (envMatch) {
    return `https://admin.${envMatch[1]}.finpay.net`;
  }

  return normalized.replace(/\/\/api\./, '//admin.');
}

export function buildFinpayPatientLink(
  apiBase: string,
  patientId: number,
  vobId?: number | null
): string {
  const adminBase = resolveAdminPortalBase(apiBase).replace(/\/$/, '');
  const path =
    vobId != null && vobId > 0
      ? `/patients/${patientId}/vob/${vobId}`
      : `/patients/${patientId}`;
  return `${adminBase}${path}`;
}

function buildEmailBody(
  form: VobWebformSubmitPayload,
  patientId: number,
  apiBase: string,
  vobId?: number | null
): Record<string, unknown> {
  return {
    first_name: form.first_name?.trim() ?? '',
    last_name_initial: lastNameInitial(form.last_name),
    phone: form.phone?.trim() ?? '',
    webform_notes: form.webform_notes?.trim() ?? '',
    patient_id: patientId,
    link_to_finpay: buildFinpayPatientLink(apiBase, patientId, vobId),
    subject: EMAIL_SUBJECT,
    preheader: EMAIL_PREHEADER,
  };
}

export async function sendVobWebformEmail(
  apiBase: string,
  authToken: string,
  sendTo: string,
  form: VobWebformSubmitPayload,
  patientId: number,
  vobId?: number | null,
  templateId: string = VOB_WEBFORM_EMAIL_TEMPLATE
): Promise<void> {
  const receiver = parseSendToAddresses(sendTo);
  if (receiver.length === 0) {
    return;
  }

  const resolvedTemplateId = templateId.trim() || VOB_WEBFORM_EMAIL_TEMPLATE;

  const response = await apiRequest(
    apiBase,
    '/configuration/v2/sendemail',
    {
      method: 'POST',
      authToken,
      body: {
        sender: DEFAULT_SENDER,
        receiver,
        template_id: resolvedTemplateId,
        ...buildEmailBody(form, patientId, apiBase, vobId),
      },
    }
  );

  if (response.hasErrors) {
    throw new Error(response.errorMessage ?? 'Failed to send notification email');
  }
}
